import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

export interface CommissionConfig {
  id: string;
  tenantId: string;
  salesPersonId: string;
  productId: string | null;
  percentage: number | null;
  value: number | null;
  validFrom: string | null;
  validUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionEntry {
  id: string;
  salesPersonId: string;
  receivableId: string;
  configId: string;
  baseAmount: number;
  commissionAmount: number;
  status: 'OPEN' | 'INCLUDED_IN_PAYOUT' | 'CANCELLED';
  payoutId: string | null;
  createdAt: string;
}

export interface CommissionPayout {
  id: string;
  tenantId: string;
  salesPersonId: string | null;
  totalAmount: number;
  status: 'DRAFT' | 'PROCESSING' | 'PAID';
  paidAt: string | null;
  createdAt: string;
}

export interface CommissionConfigInput {
  salesPersonId: string;
  productId?: string;
  percentage?: number;
  value?: number;
  validFrom?: string;
  validUntil?: string;
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['crm', 'commission-configs'] });
  queryClient.invalidateQueries({ queryKey: ['crm', 'commission-entries'] });
  queryClient.invalidateQueries({ queryKey: ['crm', 'commission-payouts'] });
}

/* RF-021/RF-023: painel, regras e pagamento consomem os endpoints reais. */
export function useCommissionConfigs(): ReturnType<typeof useQuery<CommissionConfig[]>> {
  return useQuery<CommissionConfig[]>({
    queryKey: ['crm', 'commission-configs'],
    queryFn: async () => apiClient.get<CommissionConfig[]>('/commission-configs'),
  });
}

export function useCreateCommissionConfig() {
  const queryClient = useQueryClient();
  return useMutation<CommissionConfig, unknown, CommissionConfigInput, unknown>({
    mutationFn: (input: CommissionConfigInput) =>
      apiClient.post<CommissionConfig>('/commission-configs', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useDeleteCommissionConfig() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string, unknown>({
    mutationFn: async (id: string) => apiClient.delete<void>(`/commission-configs/${id}`),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useCommissionEntries(): ReturnType<typeof useQuery<CommissionEntry[]>> {
  return useQuery<CommissionEntry[]>({
    queryKey: ['crm', 'commission-entries'],
    queryFn: async () => apiClient.get<CommissionEntry[]>('/commission-entries'),
  });
}

export function useCommissionPayouts(): ReturnType<typeof useQuery<CommissionPayout[]>> {
  return useQuery<CommissionPayout[]>({
    queryKey: ['crm', 'commission-payouts'],
    queryFn: async () => apiClient.get<CommissionPayout[]>('/commission-payouts'),
  });
}

export function useCreateCommissionPayout() {
  const queryClient = useQueryClient();
  return useMutation<CommissionPayout, unknown, { salesPersonId?: string }, unknown>({
    mutationFn: (input: { salesPersonId?: string }) =>
      apiClient.post<CommissionPayout>('/commission-payouts', input),
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function usePayCommissionPayout() {
  const queryClient = useQueryClient();
  return useMutation<CommissionPayout, unknown, string, unknown>({
    mutationFn: (id: string) => apiClient.post<CommissionPayout>(`/commission-payouts/${id}/pay`, {}),
    onSuccess: () => invalidateAll(queryClient),
  });
}
